/** `/forbidden`: explains which permission a page needs and who holds it (§3), with a way out. */
import { PERMISSIONS, ROLE_PERMISSIONS, StaffRole, type Permission } from '@windrop/contracts';
import { Button } from '@windrop/ui';
import { useLocation } from 'react-router';
import { useSession } from '../../auth/session';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { humanizeRole } from '../../shell/AppShell';
import { homePath } from '../../shell/navigation';

const isPermission = (p: string): p is Permission => (PERMISSIONS as readonly string[]).includes(p);

export const rolesHolding = (permission: Permission): StaffRole[] =>
  StaffRole.options.filter((role) => ROLE_PERMISSIONS[role].includes(permission));

export function ForbiddenPage() {
  const { session } = useSession();
  const params = new URLSearchParams(useLocation().search);
  const permissions = (params.get('permission') ?? '').split(',').filter(isPermission);
  const from = params.get('from');
  return (
    <>
      <PageHeader title="You do not have access to this page" />
      <EmptyState
        title={
          permissions.length
            ? `This page needs ${permissions.map((p) => `“${p}”`).join(' or ')}`
            : 'Permission required'
        }
        body={
          <>
            {from ? (
              <>
                <code>{from}</code> is restricted.{' '}
              </>
            ) : null}
            Your roles: {session?.roles.map(humanizeRole).join(', ') || 'none'}.
            {permissions.length ? (
              <>
                {' '}
                Roles that hold it:{' '}
                {[...new Set(permissions.flatMap(rolesHolding))].map(humanizeRole).join(', ') ||
                  'none (platform-only)'}
                . Ask your organizer admin if you need it; access changes are audited.
              </>
            ) : null}
          </>
        }
        action={<Button href={homePath(session)}>Go to your home console</Button>}
      />
    </>
  );
}

export function NotFoundPage() {
  const { session } = useSession();
  return (
    <>
      <PageHeader title="Page not found" />
      <EmptyState
        title="Nothing lives at this address"
        body="The link may be outdated, or the object belongs to another tenant (those always answer “not found”)."
        action={<Button href={homePath(session)}>Go to your home console</Button>}
      />
    </>
  );
}
